import { CircularProgress, FormControl } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FoodsSelectServerPagination } from "@foodpilot/foods";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useAllIngredientReferences } from "src/api/client-api/ingredientReference/useAllIngredientReferences";
import { useGetOneIngredientReference } from "src/api/client-api/ingredientReference/useGetOneIngredientReference";
import type { IngredientReference } from "src/api/client-api/ingredientReference/useAllIngredientReferences";

interface FormPlatformIngredientReferenceSelectorProps {
    value: string;
    onChange: (value: string) => void;
}

export const FormPlatformIngredientReferenceSelector = ({
    value,
    onChange,
}: FormPlatformIngredientReferenceSelectorProps) => {
    const { t } = useTranslation();
    const { search, delayedSearch, setSearch } = useDelayedSearch();

    const { ingredientReferences = [], isLoading } = useAllIngredientReferences({
        search: delayedSearch,
        itemsPerPage: 20,
    });

    const selectedListReference = ingredientReferences.find((reference) => reference.id.toString() === value);
    const { ingredientReference: selectedReference, isLoading: isReferenceLoading } = useGetOneIngredientReference(
        value ? Number(value) : undefined,
        { enabled: value !== "" && selectedListReference === undefined },
    );

    if (!selectedListReference && value && isReferenceLoading) {
        return <CircularProgress size={40} />;
    }

    const isSearchFound = (searchValue: string) => {
        if (delayedSearch === "") {
            return true;
        }
        return searchValue.toLowerCase().includes(delayedSearch.toLowerCase());
    };

    const options =
        selectedReference && !ingredientReferences.some((reference) => reference.id === selectedReference.id) ?
            [...ingredientReferences, selectedReference]
        :   ingredientReferences;

    return (
        <FormControl fullWidth>
            <FoodsSelectServerPagination
                selectedOption={(selectedListReference ?? selectedReference) || null}
                options={isLoading ? [] : options}
                onChange={(reference) => onChange(reference?.id.toString() ?? "")}
                getId={(reference: IngredientReference) => reference.id}
                getName={(reference: IngredientReference) => reference.name}
                textOptions={{
                    placeholder: t(
                        "settings.data_lists_overrides.reference_ingredient.select.placeholder",
                        "Select a platform reference ingredient",
                    ),
                    searchPlaceholder: t(
                        "settings.data_lists_overrides.reference_ingredient.select.searchPlaceholder",
                        "Search platform reference ingredients...",
                    ),
                    emptyOptionsText: t(
                        "settings.data_lists_overrides.reference_ingredient.select.noOptions",
                        "No platform reference ingredients available",
                    ),
                    defaultValueText: t(
                        "settings.data_lists_overrides.reference_ingredient.select.defaultValue",
                        "Default value",
                    ),
                    noResult:
                        isLoading ?
                            t("settings.data_lists_overrides.reference_ingredient.select.loading", "Loading...")
                        :   t(
                                "settings.data_lists_overrides.reference_ingredient.select.noResultFound",
                                "No platform reference ingredients found",
                            ),
                }}
                search={search}
                setSearch={setSearch}
                isSearchFound={isSearchFound}
            />
        </FormControl>
    );
};
